/**
 *  @class
 *  @function Timeline
 */

if (!customElements.get('time-line')) {
	class Timeline extends HTMLElement {
		constructor() {
			super()
			this.id = this.dataset.id
			this.layout = this.dataset.layout || 'horizontal'
		}

		connectedCallback() {
			if (this.layout === 'memory_lane') {
				this.initMemoryLane()
				return
			}

			this.initHorizontal()
		}

		disconnectedCallback() {
			if (this.onScrollBound) window.removeEventListener('scroll', this.onScrollBound)
			if (this.onResizeBound) window.removeEventListener('resize', this.onResizeBound)
		}

		/* ------------------------------------------------------------------ */
		/*  Horizontal (original) layout                                       */
		/* ------------------------------------------------------------------ */
		initHorizontal() {
			this.scroller = this.querySelector('.timeline--scroll-container')
			if (!this.scroller) return

			this.animations_enabled = document.body.classList.contains('animations-true') && typeof gsap !== 'undefined'
			this.scrollSet = false
			this.items = this.querySelectorAll('.timeline--slide')
			this.labels = this.querySelectorAll('.timeline--scroll-pagination li')

			if (this.animations_enabled) {
				this.scrollSet = gsap.quickSetter('#time-line-progress-' + this.id, 'scaleX')
				this.scrollTicking = false
				this.scroller.addEventListener(
					'scroll',
					() => {
						if (this.scrollTicking) return
						this.scrollTicking = true
						requestAnimationFrame(() => {
							this.scrollTicking = false
							this.onHorizontalScroll()
						})
					},
					{ passive: true }
				)
			}

			this.labels.forEach((item) => this.setLabelPosition(item))
		}

		setLabelPosition(item) {
			const inner = this.scroller?.querySelector('.timeline--scroll-inner')
			if (!inner || !this.items.length) return

			const contentWidth = inner.offsetWidth
			const arr = Array.prototype.slice.call(this.labels)
			const i = arr.indexOf(item)
			const itemWidth = this.items[i]?.offsetWidth || 0
			item.style.flex = itemWidth / contentWidth
		}

		onHorizontalScroll() {
			if (!this.scrollSet || !this.scroller) return
			const inner = this.scroller.querySelector('.timeline--scroll-inner')
			if (!inner) return

			const scrollLeft = this.scroller.scrollLeft
			const scrollerWidth = this.scroller.offsetWidth
			const contentWidth = inner.offsetWidth
			this.scrollSet(scrollLeft / (contentWidth - scrollerWidth))
		}

		/* ------------------------------------------------------------------ */
		/*  Memory lane — pinned scrollytelling with per-year carousels        */
		/* ------------------------------------------------------------------ */
		initMemoryLane() {
			this.track = this.querySelector('.tml-track')
			this.stage = this.querySelector('.tml-stage')
			this.groups = Array.from(this.querySelectorAll('.tml-group'))
			this.years = Array.from(this.querySelectorAll('.tml-year'))
			this.yearsViewport = this.querySelector('.tml-years')
			this.yearsList = this.querySelector('.tml-years-list')
			this.progressBar = this.querySelector('.tml-progress-bar')
			this.skipButton = this.querySelector('.tml-skip')
			this.count = this.groups.length
			this.activeIndex = 0
			this.ticking = false

			if (!this.track || this.count === 0) return

			this.initCarousels()

			this.years.forEach((year) => {
				const button = year.querySelector('.tml-year-button')
				if (!button) return
				button.addEventListener('click', () => this.goToIndex(parseInt(year.dataset.index, 10)))
			})

			if (this.skipButton) {
				this.skipButton.addEventListener('click', () => this.skipSection())
			}

			this.onScrollBound = () => this.requestUpdate()
			this.onResizeBound = () => this.handleResize()
			window.addEventListener('resize', this.onResizeBound)

			this.measureHeader()
			this.handleResize()
		}

		initCarousels() {
			this.groups.forEach((group) => {
				const slides = parseInt(group.dataset.slides, 10) || 1
				group._slideCount = slides
				group._slide = 0
				if (slides < 2) return

				this.updateSlideFocus(group)

				const prev = group.querySelector('.tml-carousel-prev')
				const next = group.querySelector('.tml-carousel-next')
				const dots = Array.from(group.querySelectorAll('.tml-carousel-dot'))

				if (prev) prev.addEventListener('click', () => this.setSlide(group, group._slide - 1))
				if (next) next.addEventListener('click', () => this.setSlide(group, group._slide + 1))
				dots.forEach((dot) =>
					dot.addEventListener('click', () => this.setSlide(group, parseInt(dot.dataset.slide, 10)))
				)
			})
		}

		setSlide(group, index) {
			const count = group._slideCount || 1
			const clamped = Math.min(count - 1, Math.max(0, index))
			group._slide = clamped
			group.style.setProperty('--tml-slide', clamped)

			group.querySelectorAll('.tml-carousel-dot').forEach((dot, i) => {
				dot.classList.toggle('is-active', i === clamped)
				dot.setAttribute('aria-current', i === clamped ? 'true' : 'false')
			})

			this.updateSlideFocus(group)
		}

		// Off-screen carousel slides are translated out of view but would otherwise
		// stay in the tab order; inert + aria-hidden keeps keyboard focus and
		// screen readers on the visible slide only.
		updateSlideFocus(group) {
			group.querySelectorAll('.tml-story').forEach((story, i) => {
				const active = i === (group._slide || 0)
				story.toggleAttribute('inert', !active)
				story.setAttribute('aria-hidden', active ? 'false' : 'true')
			})
		}

		measureHeader() {
			// Account for any sticky top chrome (announcement bar + header). Use the
			// header's bottom edge, falling back to its own height if it has scrolled off.
			const header = document.querySelector('.header-section, .header, header')
			let height = 0
			if (header) {
				const rect = header.getBoundingClientRect()
				height = Math.max(Math.round(header.offsetHeight), Math.round(rect.bottom))
			}
			this.style.setProperty('--tml-header', height + 'px')
			this.headerHeight = height
		}

		handleResize() {
			this.measureHeader()
			this.measureHold()

			// Pinned scrollytelling runs at every viewport size; the stacked layout
			// only remains as the no-JS fallback.
			if (!this.enhanced) {
				this.enhanced = true
				this.classList.add('is-enhanced')
				window.addEventListener('scroll', this.onScrollBound, { passive: true })
			}

			this.measureRail()
			this.positionRail(this.activeIndex)
			this.requestUpdate()

			const hostSection = this.closest('.shopify-section')
			if (hostSection) hostSection.classList.add('tml-parallax')
		}

		// Measure the rail so we can clamp its scroll: the year list rides up to keep the
		// active year in view, but never past the point where the last year sits at the
		// bottom (so 2026 stays low and the highlight travels down to it).
		measureRail() {
			if (!this.yearsViewport || !this.years.length) return
			this.railViewport = this.yearsViewport.clientHeight
			this.railStep = this.years[0].offsetHeight || 0
			this.railList = this.railStep * this.count
			const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
			const anchor = parseFloat(getComputedStyle(this).getPropertyValue('--tml-anchor')) || 0
			this.railAnchor = anchor * rootFont
		}

		positionRail(af) {
			if (!this.yearsList) return
			if (!this.railViewport) this.measureRail()
			let y = this.railAnchor || 0
			// Only scroll the list when it overflows the rail; otherwise leave it anchored
			// at the top and let the highlight move down through the fixed positions. `af`
			// is fractional so the list tracks scroll smoothly, and the clamp keeps the
			// last year pinned at the bottom instead of overscrolling past it.
			if (this.railList > this.railViewport) {
				const ideal = this.railAnchor - af * this.railStep
				const min = this.railViewport - this.railList
				y = Math.max(min, Math.min(this.railAnchor, ideal))
			}
			this.yearsList.style.transform = 'translateY(' + y + 'px)'
		}

		holdDistance() {
			if (typeof this.holdPx === 'number') return this.holdPx
			return this.measureHold()
		}

		measureHold() {
			const raw = getComputedStyle(this).getPropertyValue('--tml-hold').trim()
			const value = parseFloat(raw) || 0
			this.holdPx = raw.indexOf('vh') !== -1 ? (value / 100) * window.innerHeight : value
			return this.holdPx
		}

		requestUpdate() {
			if (this.ticking) return
			this.ticking = true
			window.requestAnimationFrame(() => {
				this.ticking = false
				this.update()
			})
		}

		update() {
			if (!this.enhanced) return

			const rect = this.track.getBoundingClientRect()
			const totalDistance = rect.height - this.stage.offsetHeight
			const usable = Math.max(1, totalDistance - this.holdDistance())

			const progress = Math.min(Math.max(-rect.top / usable, 0), 1)

			if (this.progressBar) {
				this.progressBar.style.transform = 'scaleY(' + progress.toFixed(4) + ')'
			}

			const maxIndex = Math.max(1, this.count - 1)
			const activeFloat = progress * maxIndex

			// The year rail glides continuously with scroll (smooth), while the images and
			// copy hard-cut to the nearest year at the midpoint (clean, no crossfade).
			this.positionRail(activeFloat)
			this.switchTo(Math.round(Math.min(this.count - 1, Math.max(0, activeFloat))))
		}

		switchTo(index) {
			if (index === this.activeIndex && this.initialized) return
			this.initialized = true
			this.activeIndex = index

			this.groups.forEach((group, i) => {
				const on = i === index
				group.style.opacity = on ? '1' : '0'
				group.style.visibility = on ? 'visible' : 'hidden'
				group.classList.toggle('is-active', on)
			})

			this.years.forEach((year, i) => {
				const isActive = i === index
				year.classList.toggle('is-active', isActive)
				const button = year.querySelector('.tml-year-button')
				if (button) button.setAttribute('aria-current', isActive ? 'true' : 'false')
			})
		}

		goToIndex(index) {
			if (Number.isNaN(index)) return

			if (!this.enhanced) {
				this.groups[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
				return
			}

			const rect = this.track.getBoundingClientRect()
			const totalDistance = rect.height - this.stage.offsetHeight
			const usable = Math.max(1, totalDistance - this.holdDistance())
			const maxIndex = this.count - 1
			const targetProgress = maxIndex <= 0 ? 0 : index / maxIndex
			const targetY = window.scrollY + rect.top + targetProgress * usable

			window.scrollTo({ top: targetY, behavior: 'smooth' })
		}

		skipSection() {
			const section = this.closest('.shopify-section')
			const next = section?.nextElementSibling

			if (next) {
				next.scrollIntoView({ behavior: 'smooth', block: 'start' })
			} else if (this.enhanced) {
				const rect = this.track.getBoundingClientRect()
				window.scrollTo({ top: window.scrollY + rect.bottom, behavior: 'smooth' })
			}
		}
	}

	customElements.define('time-line', Timeline)
}
